import { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { X, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  benefit: string;
  /** @deprecated Use the built-in checkout flow instead */
  onUpgrade?: () => void;
}

function PaywallBody({ title, benefit, onClose }: Omit<PaywallModalProps, "isOpen" | "onUpgrade">) {
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'annual'>('annual');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleUpgrade = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        'create-checkout-session',
        { body: { plan: selectedPlan, origin: window.location.origin } }
      );

      if (error || !data?.url) throw new Error('Failed to create checkout session');

      window.location.href = data.url;
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again or contact support.',
        variant: 'destructive',
      });
      setIsLoading(false);
    }
  };

  const ctaText = selectedPlan === 'annual'
    ? 'Start Dearestly Plus — USD 39.99/year'
    : 'Start Dearestly Plus — USD 4.99/month';

  return (
    <div className="flex flex-col items-center text-center px-6 pb-8 pt-2">
      <button
        onClick={onClose}
        className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity"
        aria-label="Close"
      >
        <X className="h-4 w-4" />
      </button>

      {/* Gold pill badge */}
      <span
        className="text-eyebrow inline-block mb-4 rounded-full px-4 py-1"
        style={{ backgroundColor: "#C9A84C", color: "#2D2A26" }}
      >
        Dearestly Plus
      </span>

      {/* Title */}
      <h2 className="text-page-title mb-2" style={{ color: "#2D2A26" }}>
        {title}
      </h2>

      {/* Benefit */}
      <p className="text-body mb-6" style={{ color: "#6B6560" }}>
        {benefit}
      </p>

      {/* Plan toggle */}
      <div className="flex gap-3 w-full mb-6">
        <button
          onClick={() => setSelectedPlan('monthly')}
          className="flex-1 rounded-2xl border-2 p-3 text-left transition-colors"
          style={{
            borderColor: selectedPlan === 'monthly' ? '#C9A84C' : '#D5D0C8',
            backgroundColor: selectedPlan === 'monthly' ? '#F5F0E8' : 'transparent',
          }}
        >
          <p className="text-card-heading" style={{ color: "#2D2A26" }}>Monthly</p>
          <p className="font-serif text-lg font-semibold" style={{ color: "#2D2A26" }}>USD 4.99<span className="text-xs font-normal" style={{ color: "#6B6560" }}>/mo</span></p>
        </button>
        <button
          onClick={() => setSelectedPlan('annual')}
          className="flex-1 rounded-2xl border-2 p-3 text-left transition-colors relative"
          style={{
            borderColor: selectedPlan === 'annual' ? '#C9A84C' : '#D5D0C8',
            backgroundColor: selectedPlan === 'annual' ? '#F5F0E8' : 'transparent',
          }}
        >
          <span
            className="absolute -top-2.5 right-3 px-2 py-0.5 text-[10px] font-bold rounded-full"
            style={{ backgroundColor: '#C9A84C', color: '#1A1A1A' }}
          >
            SAVE 33%
          </span>
          <p className="text-card-heading" style={{ color: "#2D2A26" }}>Annual</p>
          <p className="font-serif text-lg font-semibold" style={{ color: "#2D2A26" }}>USD 39.99<span className="text-xs font-normal" style={{ color: "#6B6560" }}>/yr</span></p>
        </button>
      </div>

      {/* CTA — gold variant for paywall (intentional, not the canonical ink) */}
      <Button
        className="w-full rounded-full text-base font-semibold py-6 motion-safe:hover:scale-[1.02]"
        style={{ backgroundColor: "#C9A84C", color: "#2D2A26" }}
        onClick={handleUpgrade}
        disabled={isLoading}
      >
        {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : ctaText}
      </Button>

      <p className="text-xs mt-3" style={{ color: "#8A857F" }}>
        Cancel any time. No contracts.
      </p>
    </div>
  );
}

export function PaywallModal({ isOpen, onClose, title, benefit }: PaywallModalProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DrawerContent style={{ backgroundColor: "#EDE9E1" }}>
          <DrawerHeader className="sr-only">
            <DrawerTitle>{title}</DrawerTitle>
            <DrawerDescription>{benefit}</DrawerDescription>
          </DrawerHeader>
          <PaywallBody title={title} benefit={benefit} onClose={onClose} />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="sm:max-w-md border-0"
        style={{ backgroundColor: "#EDE9E1" }}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{benefit}</DialogDescription>
        </DialogHeader>
        <PaywallBody title={title} benefit={benefit} onClose={onClose} />
      </DialogContent>
    </Dialog>
  );
}
